import { BulkCreateTaskDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { TaskEntity } from '@/types/api';

import { getDashboardTaskGroupsQueryKey } from './get-dashboard-task-groups';
import { getTasksQueryKey } from './get-tasks';

export interface BulkCreateTaskResult {
  tasks: TaskEntity[];
  groupId: string;
}

export const createTasks = async (
  orgId: string,
  projectId: string,
  task: BulkCreateTaskDto,
): Promise<BulkCreateTaskResult> => {
  const { data } = await api.post<unknown, ApiResponse<BulkCreateTaskResult>>(
    `/orgs/${orgId}/projects/${projectId}/tasks/bulk`,
    task,
  );

  return data;
};

export interface UseCreateTasksOptions {
  mutationConfig?: MutationConfig<(task: BulkCreateTaskDto) => Promise<BulkCreateTaskResult>>;
}

export const useCreateTasks = ({ mutationConfig }: UseCreateTasksOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds();

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationKey: ['tasks', 'create-bulk'],
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: getTasksQueryKey(orgId, projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: getDashboardTaskGroupsQueryKey(orgId, projectId),
      });
      notifications.success('tasks.create.bulkSuccess');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('tasks.create.bulkError');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (task: BulkCreateTaskDto) => {
      if (!orgId || !projectId) {
        throw new Error('Org ID and project ID are required');
      }
      return createTasks(orgId, projectId, task);
    },
  });
};
